import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

const CONF = {
  HIGH: { bg: "#FEE2E2", color: "#B91C1C" },
  MEDIUM: { bg: "#FEF3C7", color: "#92400E" },
  LOW: { bg: "#E2E8F0", color: "#475569" },
};

export function ConfidenceBadge({ confidence }) {
  const c = CONF[confidence];
  if (!c) return null;
  return (
    <span data-testid="issue-confidence-badge" title="Niveau de confiance de la détection (heuristique ≠ certitude)"
      className="ml-1.5 align-middle text-[10px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap"
      style={{ background: c.bg, color: c.color }}>
      Confiance {confidence}
    </span>
  );
}

function RedirectChain({ hops, finalUrl, finalStatus }) {
  return (
    <div data-testid="redirect-chain" className="font-mono text-[11px] text-slate-700 flex flex-wrap items-center gap-1 pb-1 border-b border-slate-200 mb-1.5">
      {(hops || []).map((h, i) => (
        <span key={i} className="inline-flex items-center gap-1">
          <span className="bg-slate-100 px-1 py-0.5 rounded break-all">{h.url}</span>
          <span className="text-amber-700 font-bold whitespace-nowrap">→ {h.status} →</span>
        </span>
      ))}
      <span className="bg-slate-100 px-1 py-0.5 rounded break-all">{finalUrl || "(destination inconnue)"}</span>
      {finalStatus !== null && finalStatus !== undefined && (
        <span className={`font-bold ${finalStatus < 400 ? "text-green-700" : "text-red-700"}`}>({finalStatus})</span>
      )}
    </div>
  );
}

const LABELS = {
  page: "Page auditée", source: "URL source", status: "Statut HTTP", http_status: "Statut HTTP",
  final_status: "Statut final", final_url: "URL finale", final_target: "Cible finale",
  canonical: "Canonical", canonicals_html: "Canonicals HTML", canonical_html: "Canonical HTML",
  canonical_http_header: "Canonical HTTP (header)", target_canonical: "Canonical de la cible",
  canonical_status: "Statut du canonical", hreflang: "Hreflang", target: "URL cible",
  targets: "URLs cibles", sources: "Source(s) de la déclaration", return_link_found: "Lien retour trouvé",
  declared: "Déclarations", signals: "Signaux détectés", word_count: "Nombre de mots",
  title: "Titre", h1: "H1", robots_meta: "Meta robots", x_robots_tag: "X-Robots-Tag",
  target_robots_meta: "Meta robots (cible)", target_x_robots_tag: "X-Robots-Tag (cible)",
  in_sitemap: "Présente dans le sitemap", internal_inbound_links: "Liens internes entrants",
  hop_limit_exceeded: "Limite de hops atteinte",
  indexability_state: "État d'indexabilité", source_domain: "Domaine source",
  target_domain: "Domaine cible", error: "Erreur", loop: "Boucle détectée",
};

function fmt(v) {
  if (v === true) return "oui";
  if (v === false) return "non";
  if (v === null || v === undefined) return "—";
  if (Array.isArray(v)) return v.map((x) => (typeof x === "object" ? JSON.stringify(x) : String(x))).join(" · ") || "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

export function IssueEvidence({ issue, idx }) {
  const [open, setOpen] = useState(false);
  const ev = issue.evidence;
  if (!ev || typeof ev !== "object") return null;
  const entries = Object.entries(ev).filter(([k]) => k !== "hops");
  const hasChain = Array.isArray(ev.hops) && ev.hops.length > 0;
  return (
    <div className="mt-1.5">
      <button onClick={() => setOpen(!open)} data-testid={`issue-evidence-toggle-${idx}`}
        className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-600 border border-slate-300 rounded-md px-2 py-0.5 hover:border-[#002FA7] hover:text-[#002FA7] transition-colors">
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {open ? "Masquer la preuve" : "Voir la preuve"}
      </button>
      {open && (
        <div data-testid={`issue-evidence-panel-${idx}`} className="mt-1.5 border border-slate-200 bg-slate-50 rounded-md p-2.5">
          {hasChain && <RedirectChain hops={ev.hops} finalUrl={ev.final_url || ev.final_target} finalStatus={ev.final_status ?? ev.status ?? null} />}
          <div className="space-y-1">
            {entries.map(([k, v]) => (
              <div key={k} className="flex gap-2 text-[11px]">
                <span className="text-slate-500 min-w-[160px] flex-shrink-0">{LABELS[k] || k}</span>
                <span className="text-slate-800 font-mono break-all">{fmt(v)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
